import { formatDate } from './dateUtils';

const EMP_TYPE_LABELS = { full_time: 'Full-Time', part_time: 'Part-Time' };
const EMP_STATUS_LABELS = {
  active: 'Active',
  inactive: 'Inactive',
  probation: 'On Probation',
  notice: 'Serving Notice',
};

const formatSalaryDisplay = (value) => {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `$${num.toLocaleString('en-SG', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

/**
 * Build labeled from→to diffs for an EmploymentMovement.
 * Shared by Career Timeline and Employment History table.
 *
 * @param {object} movement
 * @param {{ includeSalary?: boolean }} [options]
 * @returns {{ label: string, from: string|null|undefined, to: string|null|undefined }[]}
 */
export function buildMovementDiffs(movement, { includeSalary = false } = {}) {
  if (!movement) return [];

  const diffs = [];
  if (
    movement.from_department_display !== movement.to_department_display
    && (movement.from_department_display || movement.to_department_display)
  ) {
    diffs.push({
      label: 'Dept',
      from: movement.from_department_display,
      to: movement.to_department_display,
    });
  }
  if (
    movement.from_designation !== movement.to_designation
    && (movement.from_designation || movement.to_designation)
  ) {
    diffs.push({
      label: 'Designation',
      from: movement.from_designation,
      to: movement.to_designation,
    });
  }
  if (
    movement.from_trade_category_display !== movement.to_trade_category_display
    && (movement.from_trade_category_display || movement.to_trade_category_display)
  ) {
    diffs.push({
      label: 'Trade',
      from: movement.from_trade_category_display,
      to: movement.to_trade_category_display,
    });
  }
  if (
    movement.from_reporting_officer_display !== movement.to_reporting_officer_display
    && (movement.from_reporting_officer_display || movement.to_reporting_officer_display)
  ) {
    diffs.push({
      label: 'RO',
      from: movement.from_reporting_officer_display,
      to: movement.to_reporting_officer_display,
    });
  }
  if (
    movement.from_employment_type !== movement.to_employment_type
    && (movement.from_employment_type || movement.to_employment_type)
  ) {
    diffs.push({
      label: 'Type',
      from: EMP_TYPE_LABELS[movement.from_employment_type] || movement.from_employment_type,
      to: EMP_TYPE_LABELS[movement.to_employment_type] || movement.to_employment_type,
    });
  }
  if (
    movement.from_employment_status !== movement.to_employment_status
    && (movement.from_employment_status || movement.to_employment_status)
  ) {
    diffs.push({
      label: 'Status',
      from: EMP_STATUS_LABELS[movement.from_employment_status] || movement.from_employment_status,
      to: EMP_STATUS_LABELS[movement.to_employment_status] || movement.to_employment_status,
    });
  }
  if (
    includeSalary
    && movement.from_basic_salary !== movement.to_basic_salary
    && (movement.from_basic_salary != null || movement.to_basic_salary != null)
  ) {
    diffs.push({
      label: 'Salary',
      from: formatSalaryDisplay(movement.from_basic_salary),
      to: formatSalaryDisplay(movement.to_basic_salary),
    });
  }
  if (
    movement.to_probation_start_date
    && movement.from_probation_start_date !== movement.to_probation_start_date
  ) {
    diffs.push({
      label: 'Probation',
      from: movement.from_display || (movement.from_probation_start_date
        ? `From ${formatDate(movement.from_probation_start_date)}`
        : '—'),
      to: movement.to_display || `Restarts ${formatDate(movement.to_probation_start_date)}`,
    });
  } else if (movement.field_name && movement.from_value !== movement.to_value) {
    diffs.push({
      label: movement.field_name,
      from: movement.from_display || movement.from_value,
      to: movement.to_display || movement.to_value,
    });
  }
  return diffs;
}
